// @ts-nocheck
import {
  Link as TanStackLink,
  Navigate as TanStackNavigate,
  Outlet as TanStackOutlet,
  RouterProvider as TanStackRouterProvider,
  createRootRoute,
  createRoute,
  createRouter as createTanStackRouter,
  useLocation as useTanStackLocation,
  useNavigate as useTanStackNavigate,
  useParams as useTanStackParams,
  useRouter as useTanStackRouter,
  type ErrorComponentProps,
} from '@tanstack/react-router';
import {
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  type ReactElement,
  type ReactNode,
} from 'react';

export interface RouteObject {
  path?: string;
  index?: boolean;
  element?: ReactNode;
  errorElement?: ReactNode;
  children?: RouteObject[];
  caseSensitive?: boolean;
}

const RouteErrorContext = createContext<unknown>(undefined);

const renderCompatNode = (node?: ReactNode) => {
  if (node === undefined || node === null) {
    return <TanStackOutlet />;
  }

  if (isValidElement(node)) {
    return cloneElement(node as ReactElement);
  }

  return <>{node}</>;
};

const normalizeRoutePath = (path?: string, index?: boolean) => {
  if (index) {
    return '/';
  }

  if (!path) {
    return undefined;
  }

  return path.replace(/:([^/]+)/g, '$$$1');
};

const parseSearchString = (searchParams: URLSearchParams) => {
  const entries = Array.from(searchParams.entries());
  if (!entries.length) {
    return undefined;
  }

  return Object.fromEntries(entries);
};

const parseTarget = (to: string) => {
  const url = new URL(to, window.location.href);
  return {
    to: url.pathname || '/',
    search: parseSearchString(url.searchParams),
    hash: url.hash || undefined,
  };
};

const getTargetSearch = (search?: Record<string, string>) =>
  search ? `?${new URLSearchParams(search).toString()}` : '';

const isCurrentBrowserTarget = (target: { to?: string; search?: Record<string, string>; hash?: string }) => {
  const targetPath = target.to || '/';
  return (
    targetPath === window.location.pathname &&
    getTargetSearch(target.search) === window.location.search &&
    (target.hash ?? '') === window.location.hash
  );
};

const createElementComponent = (element?: ReactNode) => {
  return function CompatRouteComponent() {
    return renderCompatNode(element);
  };
};

const createErrorComponent = (errorElement?: ReactNode) => {
  if (!errorElement) {
    return undefined;
  }

  return function CompatErrorComponent({ error }: ErrorComponentProps) {
    return (
      <RouteErrorContext.Provider value={error}>
        {renderCompatNode(errorElement)}
      </RouteErrorContext.Provider>
    );
  };
};

const buildRoutes = (routes: RouteObject[], parentRoute: any): any[] => {
  return routes
    .filter((route) => route.path !== '*')
    .map((route, index) => {
      const routeId = `${(route.path ?? parentRoute.id ?? 'route').replace(/[^a-zA-Z0-9]/g, '_')}-${index}`;

      const routeConfig: any = {
        getParentRoute: () => parentRoute,
        component: createElementComponent(route.element),
        errorComponent: createErrorComponent(route.errorElement),
        caseSensitive: Boolean(route.caseSensitive),
      };

      const normalizedPath = normalizeRoutePath(route.path, route.index);

      if (normalizedPath) {
        routeConfig.path = normalizedPath;
      } else {
        routeConfig.id = routeId;
      }

      const builtRoute = createRoute(routeConfig);

      const childRoutes = route.children?.length
        ? buildRoutes(route.children, builtRoute)
        : [];

      return childRoutes.length ? builtRoute.addChildren(childRoutes) : builtRoute;
    });
};

export function createBrowserRouter(routeDefinitions: RouteObject[]) {
  const notFoundRoute = routeDefinitions.find((route) => route.path === '*');

  const rootRoute = createRootRoute({
    component: () => <TanStackOutlet />,
    notFoundComponent: notFoundRoute
      ? () => renderCompatNode(notFoundRoute.element)
      : undefined,
    errorComponent: notFoundRoute?.errorElement
      ? createErrorComponent(notFoundRoute.errorElement)
      : undefined,
  });

  const routeTree = rootRoute.addChildren(buildRoutes(routeDefinitions, rootRoute));

  return createTanStackRouter({
    routeTree,
    defaultPreload: 'intent',
    scrollRestoration: true,
  });
}

export const RouterProvider = TanStackRouterProvider;

export const Link = forwardRef(function CompatLink(props: any, ref) {
  const { to, ...rest } = props;
  const parsed = typeof to === 'string' ? parseTarget(to) : { to };

  return <TanStackLink ref={ref as never} {...rest} {...parsed} />;
});

export function Navigate(props: any) {
  const { to, ...rest } = props;
  const parsed = typeof to === 'string' ? parseTarget(to) : { to };

  if (typeof to === 'string' && isCurrentBrowserTarget(parsed)) {
    return null;
  }

  return <TanStackNavigate {...rest} {...parsed} />;
}

export function useNavigate() {
  const navigate = useTanStackNavigate({ from: '/' });
  const router = useTanStackRouter();

  return useCallback((to: string | number, options: Record<string, unknown> = {}) => {
    if (typeof to === 'number') {
      router.history.go(to);
      return;
    }

    const parsed = parseTarget(to);
    if (isCurrentBrowserTarget(parsed)) {
      return;
    }

    return navigate({
      ...parsed,
      replace: Boolean(options.replace),
      state: options.state,
    });
  }, [navigate, router]);
}

export function useParams<
  TParams extends Record<string, string | undefined> = Record<string, string | undefined>,
>(): Readonly<Partial<TParams>> {
  return (useTanStackParams({ strict: false, shouldThrow: false }) ?? {}) as Readonly<Partial<TParams>>;
}

export function useLocation() {
  const location = useTanStackLocation();
  return {
    ...location,
    key: location.href ?? `${location.pathname}${window.location.search}${window.location.hash}`,
    search: window.location.search,
    hash: window.location.hash,
  };
}

export function useOutlet() {
  return <TanStackOutlet />;
}

export function useRouteError() {
  return useContext(RouteErrorContext);
}
